import {ElasticSearchPaginationSchema} from '../../../models/ElasticSearchPaginationSchema';
import {KeyValueResponse} from '../../model';
import {AddressItem, MobileNumberItems} from '../profile/@candidate/models';
import {ApiPathParam} from '../../../models/SearchParamSchema';
import {PaginationSchema, SortingSchema} from '../../../models/PaginationSchema';
import {fileStatusTypes} from '../../document/files/api-services/model';
import {ExamGroupExamLevelFeeResponse, ExamPayment} from '../../../api-refactored/typings';
import {PaymentStatus} from '../../../models/PaymentStatus';
import {ExamPaymentMode} from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';

export interface CandidateRegisteredExamResponse {
  internalTransactionRef: string;
  registrationStatus: string;
  dateOfApplication: string;
  examLevel: string;
}

export interface CoordinatorRegisteredExamResponse {
  examRegistrationRef: string;
  coordinatorRegistrationType: string;
  dateOfApplication: string;
  examLevel: string;
}

export enum ExamRegistrationType {
  EXAM = 'EXAM',
  SUPPLEMENTARY = 'SUPPLEMENTARY',
  LATE_SUPPLEMENTARY = 'LATE_SUPPLEMENTARY',
  AMENDMENT = 'AMENDMENT',
  WITHDRAWAL = 'WITHDRAWAL',
}

export interface ExamRegistrationExist {
  examLevels?: string[];
  applicationId?: string;
  examYear?: number;
  examType?: string;
  examRegistrationType: ExamRegistrationType;
  applicationUuid: string;
}

export interface ExamRegistrationEligibilityResponse {
  draft?: ExamRegistrationExist;
  currentExamRegistrationType: ExamRegistrationType;
  hasIexamRegistration: boolean;
}

export interface ExamRegistrationEligibilityRequest {
  examType: string;
}

export interface CreateExamRegistrationRequest extends ExamRegistrationEligibilityRequest {
  token: string | null;
}

export interface TokenOnlyRequest {
  token: string | null;
}

export interface CreateExamRegistrationResponse {
  uuid: string;
  token: string;
}

export interface ExamRegistrationResponse {
  uuid: string;
  examType: KeyValueResponse;
  examRegistrationType: ExamRegistrationType;
  lastUpdatedTime: string;
  name: string | null;
}

export interface ExamProceedToPaymentRequestBody {
  uuid: string;
  paymentMode: string;
}

export interface ExamProceedToPaymentResponseBody {
  internalTransactionRef: string;
  transactionAmount?: string;
  paymentSuccess?: boolean;
}

export interface PaymentLinkRequest {
  token?: string;
}

export interface ExamTransactionInfo {
  transactionNumber: string;
  paymentTimeStamp: string;
  paymentMethod: string;
  totalFee?: string;
  internalTransactionRef: string;
  gstRegNo?: string;
  registrationUuid: string;
}

export interface ExamReviewResponse {
  candidateDetails: ExamCandidateDetails;
  selectedSubjects: ExamSelectedSubjects[];
  groupedLevelFees: ExamGroupExamLevelFeeResponse[];
  examYear: number;
  gstRegistrationNumber: string;
  payment: ExamPayment;
  className?: any;
}

export interface ExamCandidateDetails extends ContactDetailsItem {
  name?: string;
  hanyuPinyinName?: string;
  userIdType?: string;
  nricFinPassport?: string;
  dob?: string;
  gender?: string;
  race?: string;
  citizenship?: string;
  nationality?: string;
  prStatus?: string;
  nationalityIssueDate?: string;
  occupation?: string;
  schoolName?: string;
  nextOfKinName?: string;
  nextOfKin?: MobileNumberItems;
  nextOfKinRelationship?: string;
  workPassType?: string;
  finPassport?: string;
  passportNo?: string;
}

export interface ContactDetailsItem {
  email: string;
  phoneNumber?: MobileNumberItems;
  mobileNumber?: MobileNumberItems;
  mailingAddress?: AddressItem;
  foreignAddress1?: string;
  foreignAddress2?: string;
  foreignAddress3?: string;
  localAddressPreferred?: boolean;
  mobileNumberPreferred?: boolean;
  intendedResidentialArea?: string;
  intendedResidentialZone?: string;
  overseasResidenceCity?: string;
  overseasResidenceState?: string;
  overseasResidenceCountry?: string;
  overseasResidencePeriodOfResidenceFrom?: string;
  overseasResidencePeriodOfResidenceTo?: string;
}

export interface ViewSelectedSubjectPapers {
  uuid: string;
  modeOfAssessment: string;
  paperNo: string;
  selected?: boolean;
  status?: string;
}

export interface ViewSelectedSubjectsResponseBody {
  subjectUuid: string;
  subjectName: string;
  subjectCode: string;
  examSeries: string;
  examLevel: string;
  subjectLevel: string;
  language: string;
  examRegistrationDetailUuid: string;
  editable: boolean;
  mandatoryPapers: ViewSelectedSubjectPapers[] | [];
  optionalPapers: ViewSelectedSubjectPapers[] | [];
}

export interface ExamSelectedSubjects extends ViewSelectedSubjectsResponseBody {
  actionTaken: string;
  fee: string;
}

export enum ExamType {
  GCE = 'GCE',
  IPSLE = 'IPSLE',
}

export enum GenericExamLevel {
  GCE = 'GCE',
  PSLE = 'PSLE',
  IPSLE = 'IPSLE',
}

export interface Coordinator {
  coordinatorName: string;
  email: string;
  school: string;
  uuid: string;
}

export enum ApplicationStatus {
  REGISTERED = 'Registered',
  PENDING_PAYMENT = 'Pending payment',
  COMPLETED = 'Completed',
  DRAFT = 'Draft',
  WITHDRAWN = 'Withdrawn',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  PROCESSING = 'PROCESSING',
  PENDING_APPROVAL_WITHDRAW = 'Pending Approval (Withdraw)',
  PENDING_REVIEW_WITHDRAW = 'Pending Review (Withdraw)',
  WITHDRAWN_REFUND_APPROVED = 'WITHDRAWN_REFUND_APPROVED',
  WITHDRAWN_PENDING_MOE = 'WITHDRAWN_PENDING_MOE',
  WITHDRAWN_NO_REFUNDED = 'WITHDRAWN_NO_REFUNDED',
  WITHDRAWN_PENDING_REFUND = 'Withdrawn-Pending Refund',
  WITHDRAWN_REFUNDED = 'Withdrawn (Refunded)',
  SUBMITTED = 'Submitted',
  NON_NATIONAL_PENDING = 'Pending',
  NON_NATIONAL_INCOMPLETE = 'Incomplete',
  NON_NATIONAL_INELIGIBLE = 'Ineligible',
}

export interface CandidateRegisteredExamListingResponse {
  applicationID: string;
  applicationUuid: string;
  examLevels: CandidateRegisteredExamListingExamLevel[];
  applicationDateTime: string;
  entryProofLastUpdate: string;
  applicationStatus: ApplicationStatus;
  timeWindowOpen: boolean;
  examRegistrationType: ExamRegistrationType;
  examType: ExamType;
  supplementaryOpen: boolean;
  lastUpdatedDateTime: string;
}

export interface ExamRegistrationSummary extends ApplicationDetails {
}

export interface ApplicationDetails {
  applicationID: string;
  applicationDateTime: string;
  applicationStatus: string;
  examRegistrationType: ExamRegistrationType;
}

export interface CandidateRegisteredExamListingExamLevel {
  examYear: string;
  examLevel: string;
  entryProofLink?: string;
}

export interface ExamDownloadSummaryResponse {
  url: string;
}

interface ExamTransactionInfoInSummary {
  transactionNo: string;
  paymentStatus: PaymentStatus;
  internalRef: string;
}

export interface EntryProofDownloadModel {
  uuid: string;
  fileName: string;
}

export interface ExamRegisteredSummaryResponse extends ExamReviewResponse {
  applicationStatus: ApplicationDetails;
  transactionDetails: ExamTransactionInfoInSummary;
  amendmentWindowOpened: boolean;
  withdrawalWindowOpened: boolean;
  entryProofDownloadModels: EntryProofDownloadModel[];
}

export enum SubjectType {
  Private = 'PRIVATE',
  School = 'SCHOOL',
  Any = 'ANY',
}

interface ExamSubjectsWithoutOptionalPapers {
  subjectUuid: string;
  subjectName: string;
  subjectCode: string;
  examSeries: string;
  examLevel: string;
  subjectLevel: string;
  language: string;
  actionRequired: boolean;
  allowedToAdd: boolean;
  subjectType?: SubjectType;
}

export interface ExamSubjects extends ExamSubjectsWithoutOptionalPapers {
  optionalPapers?: { selected: boolean };
}

export interface AdminSelectedExamSubjects extends ExamSubjectsWithoutOptionalPapers {
  examSeriesUuid: string;
  optionalPapers?: { paperNo: string; selected: boolean; uuid: string }[];
}

export interface ExamSubjectsListResponseBody extends ElasticSearchPaginationSchema {
  content: ExamSubjects[] | [];
}

export interface SearchExamSubjectsListSchema {
  searchParam: string;
  examLevelUuid: string;
  examSeriesUuid: string[];
  languageUuid: string[];
  examType: ExamType;
  examRegistrationUuid: string;
  apiPathParam: ApiPathParam;
}

export interface ExamOptionalPapers {
  selected: boolean;
  uuid: string;
}

export interface SubjectDetails {
  subjectName: string;
  subjectLevel?: string;
  subjectCode: string;
}

export interface ExamSaveSubjectRegistrationDraftRequest {
  applicationUuid: string;
  subjectCode: string;
  examSeriesUuid: string;
  mandatoryPapers: string[] | [];
  optionalPapers: ExamOptionalPapers[] | [];
  callbackVariable: SubjectDetails;
}

export interface UpdateSubjectRegistrationDraftRequest extends SubjectRegistrationDraftRequest {
  examSeriesUuid: string;
  optionalPapers: ExamOptionalPapers[] | [];
}

export interface SubjectRegistrationDraftRequest {
  examRegistrationDetailUuid: string;
  applicationUuid: string;
  callbackVariable: SubjectDetails;
}

export interface ExamSeriesModal {
  code: string;
  description: string;
}

export interface ExamSeriesSelectedModal extends ExamSeriesModal {
  selected: boolean;
}

export interface CreateExamAmendmentRequest {
  uuid: string;
}

export interface CreateExamWithdrawalRequest {
  uuid: string;
}

export interface ViewSubjectDetailResponse {
  subjectUuid: string;
  subjectName: string;
  subjectCode: string;
  subjectLevel?: string;
  examSeries: ExamSeriesModal[];
  mandatoryPapers: ViewSelectedSubjectPapers[];
  optionalPapers: ViewSelectedSubjectPapers[];
}

export interface ViewCandidateRegisteredSubject {
  subjectUuid: string;
  subjectName: string;
  subjectCode: string;
  subjectLevel: string;
  examSeries: ExamSeriesSelectedModal[];
  mandatoryPapers: ViewSelectedSubjectPapers[];
  optionalPapers: ViewSelectedSubjectPapers[];
}

export interface ExamLevelResponse {
  uuid: string;
  token: string;
  applicationId: string;
  examLevel: string[];
  isWithdrawOpen: boolean;
}

export interface RegisteredAndDeletedSubjects {
  subjectUuid: string;
  subjectName: string;
  subjectCode: string;
  examSeries: string;
  examLevel: string;
  subjectLevel: string;
  language: string;
  examRegistrationDetailUuid: string;
  editable: boolean;
  mandatoryPapers: ViewSelectedSubjectPapers[] | [];
  optionalPapers: ViewSelectedSubjectPapers[] | [];
  nonActionable: boolean;
}

export interface RegisteredSubjectResponse {
  registeredSubjects: RegisteredAndDeletedSubjects[];
  deletedSubjects: RegisteredAndDeletedSubjects[];
}

export interface DraftRequest {
  uuid: string;
  subjectCode: string;
  callbackVariable: SubjectDetails;
}

export interface ExamSubjectAmendmentDraftRequest {
  applicationUuid?: string;
  subjectCode: string;
  examSeriesUuid: string;
  mandatoryPapers: string[] | [];
  optionalPapers: ExamOptionalPapers[] | [];
  subjectName?: string;
}

export interface UpdateSubjectAmendmentDraftRequest {
  examSeriesUuid: string;
  optionalPapers: ExamOptionalPapers[] | [];
  applicationUuid: string;
  examRegistrationDetailUuid: string;
  callbackVariable: SubjectDetails;
}

interface SubjectAmendmentResponse {
  selectedSubjects: ExamSelectedSubjects[];
  groupedLevelFees: ExamGroupExamLevelFeeResponse[];
  candidateDetails: ExamCandidateDetails;
  gstRegistrationNumber: string;
  payment: ExamPaymentMode & ExamPayment;
  examYear: number;
}

export interface SubjectAmendmentReviewResponse extends SubjectAmendmentResponse {
  paymentRequired: boolean;
  payNowProxyRequired: boolean;
}

export interface SubjectAmendmentSummaryResponse extends SubjectAmendmentResponse {
  applicationStatus: ApplicationDetails;
  transactionDetails: ExamTransactionInfoInSummary;
  amendmentWindowOpened: boolean;
  withdrawalWindowOpened: boolean;
  entryProofDownloadModels?: EntryProofDownloadModel[];
}

export interface SubjectValidationResponse {
  errorMsg: string[];
}

export interface CandidateTransactionHistoryListingResponse {
  content: CandidateTransactionHistoryListingContent[];
  pageable: PaginationSchema;
  totalElements: number;
  last: boolean;
  totalPages: number;
  size: number;
  number: number;
  sort: SortingSchema;
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface CandidateTransactionHistoryListingContent {
  applicationUuid: string;
  applicationID: string;
  examLevels: CandidateRegisteredExamListingExamLevel[];
  applicationDateTime: string;
  actionType: ActionType;
}

export enum ActionType {
  EXAMINATION_REGISTRATION = 'Examination Registration',
  SUPPLEMENTARY_REGISTRATION = 'Supplementary Registration',
  SUBJECT_AMENDMENT = 'Subject Amendment',
  EXAMINATION_WITHDRAWAL = 'Examination Withdrawal',
}

export interface GetWithdrawalResponse {
  applicationUuid: string;
  applicationID: string;
  examLevels: CandidateRegisteredExamListingExamLevel[];
  applicationDateTime: string;
  isWithdrawOpen: boolean;
}

export interface WithdrawalPostUploadDocumentsRequest {
  payload: {
    uuid: string;
    applicationType: string;
    filenames: string[];
    uploadDocumentType?: string;
  };
}

export interface WithdrawalReason {
  reasonUuid: string;
  reasonDescription: string;
  requiredDocumentInfo: string[];
  additionalTextNotice: string;
}

export interface PutWithdrawalDetailsRequestData {
  payload: {
    uuid: string;
    reason: WithdrawalReason;
    specifiedReason?: string;
  };
}

export interface EntityModelList {
  id: number;
  fileStatus: fileStatusTypes;
  uuid: string;
  fileName: string;
  fileSize: number;
  draft: boolean;
}

export interface GetWithdrawalDraftResponse {
  uuid: string;
  reason: WithdrawalReason;
  specifiedReason?: string;
  fileEntityModelList?: EntityModelList[];
  lockedByUser?: string;
}

export interface WithdrawDetails {
  reasonSelected: string;
  specifiedReason: string;
  fileNames: EntityModelList[];
  candidateDetails: {
    statutoryName: string;
    hanyuPinyinName: string;
    idType: string;
    nricFin: string;
    email: string;
    nationality: string;
    citizenship: string;
    schoolName: string;
    mailingAddress: AddressItem;
  };
  examYear: string;
  examRegistrationSubjectInfoModels: {
    subjectName: string;
    subjectCode: string;
    subjectLevel: string;
    examSeries: string;
    examLevel: string;
    languageMedium: string;
    subjectFee: string;
  }[];
  status: string;
  applicationId: string;
  applicationDateTime: string;
  lastUpdatedDateTime: string;
  payNowProxyRequired: boolean;
}

export interface WithdrawalSubmittedDetails {
  applicationId: string;
  localDateTime: string;
}

export interface ExamLevelChecking {
  examType: string;
  examRegistrationType: string;
}

export interface ExamTypeDetail {
  code: string;
  displayName: string;
}

export interface GroupWithdrawal {
  applicationUuid: string;
  applicationID: string;
  examLevels: CandidateRegisteredExamListingExamLevel[];
  applicationDateTime: string;
  lastUpdatedDateTime: string;
  submittedBy: string;
  applicationStatus: ApplicationStatus;
  withdrawalReason: string;
  closedExamLevels: string[];
}

export interface GroupWithdrawalSummary {
  applicationId: string;
  applicationStatus: string;
  applicationDateTime: string;
  lastUpdatedDateTime: string;
  coordinatorName: string;
  reasonForWithdrawal: string;
  examYear: string;
  specifiedReason?: string;
}

export interface GroupExamWithdrawalCandidate {
  candidateId: string;
  name: string;
  examLevel: string[];
  examSeries: string[];
  documents: EntityModelList[];
  uuid: string;
  documentRequired: boolean;
  noDocument: boolean;
}

export interface GroupExamWithdrawalCandidateResponseBody extends ElasticSearchPaginationSchema {
  content: GroupExamWithdrawalCandidate[] | [];
}

export interface SearchRequest {
  q?: string;
}

export interface GroupExamWithdrawalCandidateRequest extends SearchRequest {
  uuid: string;
  apiPathParam: ApiPathParam;
}

export interface GroupExamWithdrawalModalAddCandidateRequest {
  applicationUuid: string;
  uuidArray: Array<{ uuid: string }>;
}

export interface GroupExamWithdrawalModalCandidateRequest {
  uuid: string;
  q: string | null;
  apiPathParam: ApiPathParam;
  examSeries?: string;
  examLevel?: string;
}

export interface GroupExamWithdrawalDeleteCandidateRequest {
  applicationUuid: string;
  candidateUuid: string;
}

export interface SingleParentDetails {
  name?: string;
  citizenship?: string;
  documentType?: string;
  documentNumber?: string;
  workPassType?: string;
  finNumber?: string;
}

export interface ParentDetails {
  fatherDetail?: SingleParentDetails;
  motherDetail?: SingleParentDetails;
}

export enum NonNationalExamType {
  AEIS = 'AEIS',
  SAEIS = 'SAEIS',
  SPERS = 'SPERS',
  SPE = 'SPE',
}

export type IOptionType = {
  code: string;
  description: string;
};

export interface AdmissionExamLevelRequest {
  examType: NonNationalExamType;
}

export interface AdmissionExamLevelResponse {
  examType: KeyValueResponse;
  admissionLevelVacancies: KeyValueResponse[];
}

export interface CreateNonExamRegistrationRequest {
  token: string | null;
  examType: string;
  admissionLevelCode: string;
}

export interface NonNationalConfirmAndPayResponse {
  examYear?: number;
  examLevel?: string;
  admissionLevel?: string;
  personDeclaring?: string;
  personDeclaringId?: string;
  mtLanguage?: string;
  candidateInfo?: {
    name?: string;
    hanyuPinyinName?: string;
    userIdType?: string;
    nricFinPassport?: string;
    passportNo?: string;
    dob?: string;
    gender?: string;
    race?: string;
    citizenship?: string;
    nationality?: string;
    prStatus?: string;
    nationalityIssueDate?: string;
    occupation?: string;
    schoolName?: string;
    nextOfKinName?: string;
    nextOfKin?: MobileNumberItems;
    nextOfKinRelationship?: string;
    email: string;
    phoneNumber?: MobileNumberItems;
    mobileNumber?: MobileNumberItems;
    mailingAddress?: AddressItem;
    foreignAddress1?: string;
    foreignAddress2?: string;
    foreignAddress3?: string;
    localAddressPreferred?: boolean;
    mobileNumberPreferred?: boolean;
    birthCertNo?: string;
    sprAttainedDate?: string;
    workPassType?: string;
    intendedResidentialArea?: string;
    intendedResidentialZone?: string;
    overseasResidenceCity?: string;
    overseasResidenceCountry?: string;
    overseasResidenceState?: string;
    overseasResidencePeriodOfResidenceFrom?: string;
    overseasResidencePeriodOfResidenceTo?: string;
  };
  fatherDetail?: {
    name?: string;
    citizenship?: string;
    documentType?: string;
    documentNumber?: string;
    workPassType?: string;
    finNumber?: string;
  };
  motherDetail?: {
    name?: string;
    citizenship?: string;
    documentType?: string;
    documentNumber?: string;
    workPassType?: string;
    finNumber?: string;
  };
  caeCertificationDetail?: {
    type?: string;
    centreReferenceNo?: string;
    verificationNo?: string;
    country?: string;
    dateTaken?: string;
  };
  ceqScoreDetail?: {
    overall?: number;
    reading?: number;
    writing?: number;
    listening?: number;
    speaking?: number;
  };
  schoolDetails?: {
    schoolName?: string;
    type?: string;
    country?: string;
    dateOfAdmission?: string;
    dateOfCompleting?: string;
    highestLevelPassed?: string;
  }[];
  siblingDetails?: {
    name?: string;
    gender?: string;
    dateOfBirth?: string;
    level?: string;
  }[];
  supportingDocument?: string[];
  payment: ExamPaymentMode & ExamPayment;
  gstRegistrationNumber: string;
}

export interface NonNationalExamSummaryResponse extends NonNationalConfirmAndPayResponse {
  applicationStatus: {
    applicationID?: string;
    applicationDateTime?: string;
    applicationStatus?: string;
    examRegistrationType?: string;
    remarks?: string;
  };
  entryProofDownloadModels: {
    uuid: string;
    fileName: string;
  }[];
}

export interface NonExamRegistrationExist {
  admissionLevelCode: string;
  applicationId: string;
  applicationUuid: string;
  examLevels: string;
  examRegistrationType: 'SUPPLEMENTARY';
  examType: string;
  examYear: string;
}

export interface NonExamRegistrationEligibilityResponse {
  draft?: NonExamRegistrationExist;
  currentExamRegistrationType: ExamRegistrationType;
  hasIexamRegistration: boolean;
}

export interface ParentParticularDetails {
  parentType: string;
  declaringPerson: {
    name: string;
    uinFinPassport: string;
  };
  fatherDetail?: {
    name?: string;
    citizenship?: string;
    documentType?: string;
    documentNumber?: string;
    workPassType?: string;
    fin?: string;
  };
  motherDetail?: {
    name?: string;
    citizenship?: string;
    documentType?: string;
    documentNumber?: string;
    workPassType?: string;
    fin?: string;
  };
  lockedByUser?: string;
}
